import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignIn, useSignIn } from "@clerk/react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const IdPasswordForm = () => {
  const { signIn } = useSignIn();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/mlm/login", { identifier, password });
      const r1 = await signIn.ticket({ ticket: data.ticket });
      if (r1.error) throw new Error(r1.error.message);
      const r2 = await signIn.finalize();
      if (r2.error) throw new Error(r2.error.message);
      navigate("/member");
    } catch (err) {
      setError(err.response ? errMsg(err) : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
      setPassword("");
    }
  };
  return (
    <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="member-id-login-form">
      <p className="font-display text-lg font-bold">Sign in with Distributor ID</p>
      <div className="space-y-1.5"><Label htmlFor="ident">Distributor ID or email</Label><Input id="ident" autoComplete="username" placeholder="BMID000001 or you@email.com" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required data-testid="member-login-identifier" /></div>
      <div className="space-y-1.5"><Label htmlFor="pw">Password</Label><Input id="pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="member-login-password" /></div>
      {error && <p role="alert" className="text-sm text-red-600" data-testid="member-login-error">{error}</p>}
      <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-amber-600 hover:bg-amber-700" data-testid="member-login-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</Button>
      <p className="text-xs text-slate-500">Signed up with Google? Sign in with Google below once, then set a password under Overview → Login password.</p>
    </form>
  );
};

export default function MemberLogin() {
  return (
    <div className="container-x grid gap-10 py-16 lg:grid-cols-2">
      <div className="space-y-6">
        <div className="space-y-4">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Member login</p>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">Distributor dashboard</h1>
          <p className="max-w-md text-slate-600">Sign in with your Distributor ID (or email) and password to order product packages, track orders and see your team, sales and wallet.</p>
        </div>
        <IdPasswordForm />
      </div>
      <div className="space-y-3" data-testid="member-login-form">
        <p className="text-sm font-semibold text-slate-600">New here, or prefer email code / Google?</p>
        <SignIn routing="hash" withSignUp forceRedirectUrl="/member" signUpForceRedirectUrl="/member" />
      </div>
    </div>
  );
}
